import type { BaseEntity, SimpleTableConfig } from "../UniversalCRMTable/types";
import { cleanFieldValue } from "./form-data-cleaner/field-cleaners";

/**
 * Output type of the cleaner. Per-field invariants (locked in by
 * `tests/unit/form-data-cleaner.test.ts` and
 * `tests/unit/form-data-cleaner-branches.test.ts`):
 *
 *   - `type: "number"`   → `number | undefined` (never a string; downstream
 *                          Zod schemas like `DealsInsertInputSchema` expect
 *                          `z.number()`. DECIMAL/BigFloat string serialization
 *                          happens at the GraphQL mutation boundary.)
 *   - `type: "boolean"`  → `boolean`
 *   - `type: "tags"`     → `string[]` (empty strings filtered)
 *   - `type: "multi-user"` → `string[]` (user-id strings)
 *   - `type: "array"`    → `string[]` (deduped, trimmed)
 *   - `type: "date" | "datetime" | "due-date" | "time" | "date-worked" | "client-select"`
 *                        → `string | null`
 *   - `type: "text" | "textarea"` → `string | undefined`
 *   - `type: "json"`     → entity-specific shape (line items, raw json, etc.)
 *
 * The return type stays a loose `Record<string, unknown>` because per-entity
 * shape is determined at runtime from the config; entity-specific Zod schemas
 * in the mutation hooks (e.g. `DealsInsertInputSchema.parse`) enforce the
 * final contract.
 *
 * Per-field-type normalization lives in `form-data-cleaner/field-cleaners.ts`
 * (and the two line-item shapes in `form-data-cleaner/line-items.ts`); this
 * module owns the ORDER of the passes, which is load-bearing — see the
 * comments on each pass below.
 */
export type CleanedFormData = Record<string, unknown>;

/**
 * Columns the database manages itself and a create/edit form must NEVER send.
 *
 * `created_at` / `updated_at` exist as *display* fields in most entity configs
 * (type: "date", editable: false) so they can be shown, sorted, and filtered in
 * the table. But they are DB-managed: every table defaults them to `now()` (and
 * `deals.created_at` / both `contracts` columns are additionally NOT NULL).
 *
 * When such a field is empty on create, the date branch coerces it to `null`
 * (`value || null`). Because the key IS in `config.fields`, the
 * "remove-unknown-keys" pass does not strip it, so `{ created_at: null }`
 * reaches pg_graphql. Postgres only applies a column DEFAULT when the column is
 * OMITTED — an explicit `null` overrides it — so the insert fails on NOT NULL
 * columns (deals) and silently stores `null` instead of `now()` on the rest.
 *
 * Stripping these here is the single, entity-agnostic root-cause fix: a form
 * never owns these values. (The import path uses a different cleaner in
 * `lib/utils/crm/form-helpers` and is intentionally unaffected.)
 */
export const DB_MANAGED_TIMESTAMP_FIELDS = ["created_at", "updated_at"] as const;

/**
 * Create a form data cleaner function.
 * Pure function factory: config + appUserId → (data) => cleanedData
 */
export function createFormDataCleaner<T extends BaseEntity = BaseEntity>(
  config: SimpleTableConfig<T>,
  appUserId?: string
): (data: Record<string, unknown>) => CleanedFormData {
  return function cleanFormData(data: Record<string, unknown>): CleanedFormData {
    const cleanedData: Record<string, unknown> = { ...data };

    // Pass 1 — normalize each declared field by its configured type.
    for (const [fieldName, field] of Object.entries(config.fields)) {
      const result = cleanFieldValue(field, data[fieldName]);
      if (result.action === "set") cleanedData[fieldName] = result.value;
      else if (result.action === "delete") delete cleanedData[fieldName];
    }

    // Pass 2 — remove fields the config marks as form-only.
    for (const [fieldName, field] of Object.entries(config.fields)) {
      if (field?.excludeFromApi === true) {
        delete cleanedData[fieldName];
        console.debug(`[cleanFormData] Excluded field from API: ${fieldName}`);
      }
    }

    // Pass 3 — remove any key not declared in the config (e.g. GraphQL nested
    // objects like tickets, companies, app_users that came back with the row).
    for (const fieldName of Object.keys(cleanedData)) {
      if (!config.fields[fieldName]) {
        delete cleanedData[fieldName];
        console.debug(`[cleanFormData] Removed undefined field from API: ${fieldName}`);
      }
    }

    // Pass 4 — strip DB-managed timestamp columns. A form never owns these.
    // They live in the config only as display fields; sending them (as null
    // from the date branch) overrides the DB `now()` default and violates NOT
    // NULL on deals/contracts. See DB_MANAGED_TIMESTAMP_FIELDS.
    for (const field of DB_MANAGED_TIMESTAMP_FIELDS) {
      delete cleanedData[field];
    }

    // Pass 5 — add the owning user id LAST, so pass 3 (which would not find it
    // in `config.fields`) cannot strip it again.
    if (config.autoIncludeUserId && appUserId) {
      // Use created_by for tickets entity, user_id for others
      const userIdField = config.entity === "tickets" ? "created_by" : "user_id";
      cleanedData[userIdField] = appUserId;
    }

    return cleanedData;
  };
}
